import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ExternalLink, Sparkles } from "lucide-react";
import Reveal from "../motion/Reveal";
import { references } from "../../data/content";

export default function References() {
  return (
    <section
      id="referenzen"
      className="relative bg-white py-32 lg:py-44"
      data-testid="references-section"
    >
      <div className="max-w-[90rem] mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8 mb-16 lg:mb-24">
          <div className="max-w-2xl">
            <Reveal>
              <p className="eyebrow">Referenzen · Case Studies</p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-display font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-balance">
                Ausgewählte Arbeiten.
                <br />
                <span className="text-brand-muted">Ehrlich dokumentiert.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <a
              href="#kontakt"
              className="inline-flex items-center gap-2 text-sm font-medium text-brand-primary hover:text-brand-accent transition-colors"
              data-testid="references-cta"
            >
              Projekt anfragen <ArrowUpRight className="h-4 w-4" />
            </a>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          {references.map((r, i) => (
            <Reveal
              key={r.id}
              delay={i * 0.1}
              className={
                i === 0
                  ? "lg:col-span-7"
                  : i === 1
                    ? "lg:col-span-5"
                    : "lg:col-span-12"
              }
            >
              <ReferenceCard {...r} featured={i === 2} index={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ReferenceCard(props) {
  const {
    name,
    subtitle,
    year,
    role,
    body,
    image,
    imageMode = "cover",
    website,
    status,
    results,
    technologies,
    featured,
    index,
  } = props;

  if (imageMode === "logo") {
    return <LogoCaseCard {...props} />;
  }

  // Cover-image style card (fox-COURIER)
  const aspect = featured ? "aspect-[16/9] lg:aspect-[21/10]" : "aspect-[4/3] lg:aspect-[5/4]";

  return (
    <motion.article
      whileHover="hover"
      initial="initial"
      className={`group relative overflow-hidden rounded-3xl bg-brand-primary text-white transition-shadow duration-500 shadow-[0_20px_50px_-25px_rgba(15,23,42,0.35)] hover:shadow-[0_45px_90px_-30px_rgba(15,23,42,0.55)] ${aspect}`}
      data-testid={`reference-card-${name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <motion.img
        src={image}
        alt={`${name} — Referenzprojekt Metalotsen`}
        loading="lazy"
        decoding="async"
        variants={{ initial: { scale: 1.02 }, hover: { scale: 1.07 } }}
        transition={{ duration: 1.4, ease: [0.19, 1, 0.22, 1] }}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div
        aria-hidden
        className="absolute inset-0 transition-opacity duration-500 group-hover:opacity-95"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,23,42,0.10) 0%, rgba(15,23,42,0.40) 50%, rgba(15,23,42,0.94) 100%)",
        }}
      />

      <div className="relative p-8 lg:p-12 flex items-start justify-between gap-4">
        <span className="eyebrow text-brand-accent max-w-[70%]">{subtitle}</span>
        <div className="text-right shrink-0">
          {status && <StatusPill status={status} />}
          <div className="text-white/60 text-xs tracking-widest tabular-nums mt-2">
            {String(index + 1).padStart(2, "0")} · {year}
          </div>
        </div>
      </div>

      <motion.div
        variants={{ initial: { y: 0 }, hover: { y: -6 } }}
        transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
        className="absolute inset-x-0 bottom-0 p-8 lg:p-12"
      >
        <h3 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.98]">
          {name}
        </h3>
        <p className="mt-4 text-white/85 max-w-2xl leading-relaxed text-base lg:text-lg">
          {body}
        </p>

        <CaseStripe results={results} technologies={technologies} />

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-white/10">
          <div className="text-white/70 text-xs uppercase tracking-widest">
            {role}
          </div>
          {website && <WebsiteLink name={name} website={website} />}
        </div>
      </motion.div>
    </motion.article>
  );
}

/**
 * Logo-Case-Card: zweigeteilte Karte.
 *  - Oberer Bereich: heller Hintergrund mit dem Original-Kundenlogo, groß & sauber.
 *  - Unterer Bereich: dunkler Case-Study-Inhalt.
 */
function LogoCaseCard({
  name,
  subtitle,
  year,
  role,
  body,
  image,
  website,
  status,
  results,
  technologies,
  index,
}) {
  return (
    <motion.article
      whileHover="hover"
      initial="initial"
      className="group relative overflow-hidden rounded-3xl bg-brand-primary text-white flex flex-col shadow-[0_20px_50px_-25px_rgba(15,23,42,0.35)] hover:shadow-[0_45px_90px_-30px_rgba(15,23,42,0.55)] transition-shadow duration-500 min-h-[620px] lg:min-h-[680px]"
      data-testid={`reference-card-${name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {/* Header: logo showcase */}
      <div className="relative h-[42%] min-h-[240px] lg:min-h-[280px] bg-gradient-to-br from-white via-[#F8FAFC] to-[#EEF2F7] flex items-center justify-center overflow-hidden border-b border-black/5">
        {/* faint accent */}
        <div
          aria-hidden
          className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full"
          style={{
            background:
              "radial-gradient(circle at center, rgba(14,165,233,0.18) 0%, rgba(14,165,233,0) 65%)",
            filter: "blur(30px)",
          }}
        />
        <motion.img
          src={image}
          alt={`${name} Logo`}
          loading="lazy"
          decoding="async"
          variants={{ initial: { scale: 1 }, hover: { scale: 1.04 } }}
          transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
          className="relative max-h-[70%] max-w-[68%] object-contain"
        />

        {/* top row overlay */}
        <div className="absolute inset-x-0 top-0 p-6 lg:p-8 flex items-start justify-between gap-4">
          <span className="text-[11px] uppercase tracking-widest font-semibold text-brand-accent">
            {subtitle}
          </span>
          <div className="text-right shrink-0">
            {status && <StatusPill status={status} light />}
            <div className="text-brand-muted text-xs tracking-widest tabular-nums mt-2">
              {String(index + 1).padStart(2, "0")} · {year}
            </div>
          </div>
        </div>
      </div>

      {/* Body: case study */}
      <motion.div
        variants={{ initial: { y: 0 }, hover: { y: -4 } }}
        transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
        className="relative flex-1 p-8 lg:p-12 flex flex-col"
      >
        <h3 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.98]">
          {name}
        </h3>
        <p className="mt-4 text-white/80 leading-relaxed text-base lg:text-lg max-w-2xl">
          {body}
        </p>

        <CaseStripe results={results} technologies={technologies} />

        <div className="mt-auto pt-6 flex flex-wrap items-center justify-between gap-4 border-t border-white/10">
          <div className="text-white/70 text-xs uppercase tracking-widest">
            {role}
          </div>
          {website && <WebsiteLink name={name} website={website} />}
        </div>
      </motion.div>
    </motion.article>
  );
}

function StatusPill({ status, light = false }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1 rounded-full border backdrop-blur-sm ${
        light
          ? "border-brand-border text-brand-primary/85 bg-white/70"
          : "border-white/20 text-white/85"
      }`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-brand-success" />
      {status}
    </span>
  );
}

function CaseStripe({ results, technologies }) {
  if (!results?.length && !technologies?.length) return null;
  return (
    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 max-w-3xl">
      {results?.length ? (
        <div>
          <p className="text-[10.5px] uppercase tracking-widest text-white/50 font-semibold flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-brand-accent" />
            Ergebnisse
          </p>
          <ul className="mt-2 space-y-1 text-white/90 text-sm">
            {results.map((r) => (
              <li key={r} className="tabular-nums">
                · {r}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {technologies?.length ? (
        <div>
          <p className="text-[10.5px] uppercase tracking-widest text-white/50 font-semibold">
            Eingesetzt
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {technologies.map((t) => (
              <span
                key={t}
                className="text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-white/15 text-white/85 backdrop-blur-sm"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function WebsiteLink({ name, website }) {
  return (
    <a
      href={website}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-accent hover:text-white transition-colors"
      data-testid={`reference-link-${name.toLowerCase().replace(/\s+/g, "-")}`}
      aria-label={`Website von ${name} in neuem Tab öffnen`}
    >
      Website besuchen
      <ExternalLink className="h-3.5 w-3.5" />
    </a>
  );
}
